import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ProspectForm } from './ProspectForm';
import { prospectSchema, type ProspectFormData } from '@/lib/validations/prospect';
import { useProspects } from '@/hooks/useProspects';

interface AddProspectDialogProps {
  trigger?: React.ReactNode;
}

export function AddProspectDialog({ trigger }: AddProspectDialogProps) {
  const [open, setOpen] = useState(false);
  const { addProspect } = useProspects();

  const form = useForm<ProspectFormData>({
    resolver: zodResolver(prospectSchema),
    defaultValues: {
      company: '',
      contactName: '',
      email: '',
      phone: '',
      title: '',
      stage: 'new',
      notes: '',
      isPrimaryContact: false,
    },
  });

  const onSubmit = async (data: ProspectFormData) => {
    try {
      await addProspect(data);
      toast.success('Prospect added successfully');
      form.reset();
      setOpen(false);
    } catch (error) {
      console.error('Failed to add prospect:', error);
      toast.error('Failed to add prospect. Please try again.');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Prospect
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Prospect</DialogTitle>
          <DialogDescription>
            Fill in the details below to add a new prospect to your pipeline.
          </DialogDescription>
        </DialogHeader>
        
        <ProspectForm form={form} onSubmit={onSubmit}>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Adding...' : 'Add Prospect'}
            </Button>
          </div>
        </ProspectForm>
      </DialogContent>
    </Dialog>
  );
}